import { useEffect, useState } from "react";
import { BrandIcon, BrandTile } from "../../components/BrandIcon";
import { Icon } from "../../components/Icon";
import { type CatalogConnector } from "../../../shared/connector-catalog";
import { hasWorkspaceKey, type AgentDTO, type ConnectorDTO } from "../../../shared/types";
import { confirmDialog, Dialog } from "../../components/Dialog";
import { List, ListRow } from "../../components/List";
import { policySummary } from "../../components/PolicyEditor";
import { TextLink } from "../../components/TextLink";
import { AgentLineWarning, Badge, Button, ErrorText, Field, inputCls, PageHeader } from "../../components/ui";
import { del, get, patch, post } from "../../lib/api";
import { usedByLine } from "../../lib/connectors";
import { navigate, useLocation } from "../../lib/router";
import { Switch } from "../../components/Switch";
import { SecretInputs } from "./ConnectorKey";
import { ManageConnector } from "./ConnectorManage";

/** The legacy Google Drive connector: one admin's login shared by everyone, the opposite of D2. Kept until Dimi's go to delete it. */
const isLegacyDrive = (c: ConnectorDTO) => c.authConfig.catalog === "google-drive" || c.slug === "google-drive";
const LEGACY_DRIVE_REASON = "To be deleted: one admin's Google login shared by everyone. Google Workspace acts as each person instead.";
/** A tool that declares a way in for people may be added without a workspace key. */
const keyOptional = (entry: CatalogConnector) => Boolean(entry.personal?.signIn || entry.personal?.token);

export function AdminConnectors() {
  const [connectors, setConnectors] = useState<ConnectorDTO[]>([]);
  const [catalog, setCatalog] = useState<CatalogConnector[]>([]);
  const [picking, setPicking] = useState(false);
  const [adding, setAdding] = useState<CatalogConnector | null>(null);
  const [addValues, setAddValues] = useState<Record<string, string>>({});
  const [addName, setAddName] = useState("");
  const [addParams, setAddParams] = useState<Record<string, string>>({});
  const [me, setMe] = useState<string | null>(null);
  const [custom, setCustom] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [agents, setAgents] = useState<AgentDTO[]>([]);

  // The connector being managed, and its tab, live in the address: a link opens the pop-up where it was.
  const { search } = useLocation();
  const editing = connectors.find((c) => c.slug === search.get("connector")) ?? null;
  const setEditing = (c: ConnectorDTO | null) => (c ? navigate(`/admin/connectors?connector=${encodeURIComponent(c.slug)}`) : navigate("/admin/connectors", true));

  const load = () => get<ConnectorDTO[]>("/api/connectors").then(setConnectors, () => {});

  useEffect(() => {
    load();
    get<AgentDTO[]>("/api/agents").then(setAgents, () => {});
    get<{ user: { id: string } }>("/api/me").then((r) => setMe(r.user.id), () => {});
    get<CatalogConnector[]>("/api/connectors/catalog").then(setCatalog, () => {});
    const q = new URLSearchParams(location.search);
    if (q.get("connected")) setNotice(`Connected ${q.get("connected")}`);
    if (q.get("oauth_error")) setNotice(`OAuth failed: ${q.get("oauth_error")}`);
  }, []);

  const entryFor = (c: ConnectorDTO) => catalog.find((e) => e.key === c.authConfig.catalog);
  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <PageHeader
        title="Connectors"
        subtitle="Pick a service and paste its key. Agents call it through the proxy; keys are encrypted and never shown again."
        actions={<Button variant="primary" onClick={() => setPicking(true)}>Add connector</Button>}
      />
      {notice && <p className="mx-4 mt-3 rounded bg-success/10 px-3 py-2 text-sm text-success md:mx-6">{notice}</p>}

      <div className="p-4 md:p-6">
        <List
          empty={
            <>
              No connectors yet. <TextLink onClick={() => setPicking(true)}>Add your first one</TextLink>.
            </>
          }
        >
          {[...connectors]
            .sort((a, b) => {
              const attention = (c: ConnectorDTO) => (!c.enabled || !hasWorkspaceKey(c) || c.needsReconnect ? 0 : 1);
              return attention(a) - attention(b) || a.name.localeCompare(b.name);
            })
            .map((c) => {
              const entry = entryFor(c);
              const usedBy = agents.filter((a) => a.connectorIds.includes(c.id));
              const restricted = policySummary(c.policy);
              const subtitle =
                typeof c.authConfig.github_account === "string"
                  ? `github.com/${c.authConfig.github_account}`
                  : c.authType === "google_service_account"
                    ? "Acts as the person asking — their Drive, Calendar and Gmail"
                    : entry?.description ?? c.baseUrl;
              const accessLabel = !restricted && c.levels.length === 0 ? "Full access" : `${restricted ? "Limited" : "Full"} by default${c.levels.length ? ` · +${c.levels.length} level${c.levels.length === 1 ? "" : "s"}` : ""}`;
              const accessTitle = [`Everyone: ${restricted ?? "no restrictions"}`, ...c.levels.map((l) => `${l.name}: ${policySummary(l.policy) ?? "no restrictions"}`)].join("\n");
              return (
                <ListRow
                  key={c.id}
                  leading={<BrandTile brand={entry?.key ?? c.slug} />}
                  title={
                    <>
                      <span className="truncate font-medium">{c.name}</span>
                      {!c.hasSecrets && c.credentials === "shared" && <Badge tone="amber">no key</Badge>}
                      {c.needsReconnect && (c.workspaceMode === "signin_app"
                        ? <span title={`${c.name}'s app token was refused`}><Badge tone="red">app token refused</Badge></span>
                        : <Badge tone="red">reconnect</Badge>)}
                      {!c.enabled && <Badge tone="amber">disabled</Badge>}
                      {(c.hasSecrets || c.credentials !== "shared") && !c.needsReconnect && c.enabled && <Badge tone="emerald">ready</Badge>}
                      {entry?.hidden && !isLegacyDrive(c) && <Badge>legacy</Badge>}
                      {isLegacyDrive(c) && <span title={LEGACY_DRIVE_REASON}><Badge tone="red">to be deleted</Badge></span>}
                      {c.credentials === "own" && <Badge tone="blue">people's own login</Badge>}
                      {c.credentials === "internal" && <Badge tone="blue">through Pandas</Badge>}
                    </>
                  }
                  subtitle={<span title={subtitle}>{subtitle}</span>}
                  trailing={
                    <>
                      <span
                        title={accessTitle}
                        className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 whitespace-nowrap ${restricted || c.levels.length ? "border-blue-200 bg-info/10 text-info dark:border-blue-900" : "border-line text-muted"}`}
                      >
                        <Icon name="audit" className="size-3.5" /> {accessLabel}
                      </span>
                      <span className="whitespace-nowrap" title={usedBy.map((a) => a.name).join(", ")}>
                        {usedByLine(c.platformUses ?? [], usedBy.length)}
                      </span>
                      <Button
                        onClick={() => {
                          setError(null);
                          setEditing(c);
                        }}
                      >
                        Manage
                      </Button>
                    </>
                  }
                />
              );
            })}
        </List>
      </div>

      {/* Step 1: choose a service */}
      <Dialog open={picking} onClose={() => setPicking(false)} size="xl" title="Add connector">
        <List>
          {catalog
            .filter((entry) => !entry.hidden)
            .map((entry) => {
              const exists = connectors.some((c) => c.slug === entry.key);
              return (
                <ListRow
                  key={entry.key}
                  className={exists ? "bg-fill-subtle" : ""}
                  leading={<BrandTile brand={entry.key} />}
                  title={<span className="truncate font-medium">{entry.name}</span>}
                  subtitle={<span title={entry.description}>{entry.description}</span>}
                  trailing={
                    exists ? (
                      <Badge>Added</Badge>
                    ) : (
                      <Button
                        variant="primary"
                        onClick={() => {
                          setPicking(false);
                          setAdding(entry);
                          setAddName(entry.name);
                          setAddValues({});
                          setAddParams({});
                          setError(null);
                        }}
                      >
                        Add
                      </Button>
                    )
                  }
                />
              );
            })}
          <ListRow
            leading={<BrandTile brand="custom" />}
            title={<span className="truncate font-medium">Custom API</span>}
            subtitle="Any HTTP API with a token, header key or OAuth2 (advanced)."
            trailing={
              <Button
                onClick={() => {
                  setPicking(false);
                  setCustom(true);
                  setError(null);
                }}
              >
                Add
              </Button>
            }
          />
        </List>
      </Dialog>

      {/* Step 2: paste the key */}
      <Dialog
        open={Boolean(adding)}
        onClose={() => setAdding(null)}
        title={adding && <span className="flex items-center gap-2"><BrandIcon brand={adding.key} className="size-5" /> Connect {adding.name}</span>}
        busy={busy}
        primary={
          adding && (
            <Button
              variant="primary"
              disabled={busy || (adding.secretFields.some((f) => !f.optional && !addValues[f.key]?.trim()) && !(keyOptional(adding) && adding.secretFields.every((f) => !addValues[f.key]?.trim()))) || (adding.params ?? []).some((p) => !addParams[p.key]?.trim())}
              onClick={() =>
                run(async () => {
                  const created = await post<ConnectorDTO>("/api/connectors/from-catalog", { key: adding.key, name: addName, secrets: addValues, params: addParams });
                  setAdding(null);
                  await load();
                  setEditing(created);
                })
              }
            >
              {busy ? "Saving…" : "Save connector"}
            </Button>
          )
        }
      >
        {adding && (
          <div className="space-y-3">
            <p className="text-sm text-muted">{adding.credentialHelp}</p>
            <TextLink href={adding.docsUrl} className="text-xs">
              API docs
            </TextLink>
            <Field label="Display name">
              <input className={inputCls} value={addName} onChange={(e) => setAddName(e.target.value)} />
            </Field>
            {(adding.params ?? []).map((p) => (
              <Field key={p.key} label={p.label} hint={p.help}>
                <input
                  className={`${inputCls} font-mono`}
                  placeholder={p.placeholder}
                  value={addParams[p.key] ?? ""}
                  onChange={(e) => setAddParams({ ...addParams, [p.key]: e.target.value })}
                  autoComplete="off"
                />
              </Field>
            ))}
            {keyOptional(adding) && (
              <p className="rounded-xl bg-fill-subtle px-3 py-2 text-sm">
                The workspace key is optional for {adding.name}: leave it empty if only people's own logins will be used. Agents working on their own need it.
              </p>
            )}
            <SecretInputs fields={adding.secretFields} values={addValues} onChange={setAddValues} />
            <ErrorText error={error} />
          </div>
        )}
      </Dialog>

      {/* Manage an existing connector */}
      <Dialog
        open={Boolean(editing)}
        onClose={() => setEditing(null)}
        size="xl"
        title={editing && <span className="flex items-center gap-2"><BrandIcon brand={entryFor(editing)?.key ?? editing.slug} className="size-5" /> {editing.name}</span>}
        headerAction={
          editing && (
            <Switch
              aria-label={`${editing.name} on`}
              checked={editing.enabled}
              disabled={busy}
              onChange={(enabled) => run(async () => { await patch(`/api/connectors/${editing.id}`, { enabled }); await load(); })}
            />
          )
        }
        primary={<Button onClick={() => setEditing(null)}>Close</Button>}
        cancel={false}
        secondary={
          editing && (
            <>
              <Button
                variant="danger"
                onClick={() =>
                  run(async () => {
                    if (!(await confirmDialog({ title: `Delete ${editing.name}?`, body: "Agents lose access to it immediately.", confirm: "Delete connector", danger: true }))) return;
                    await del(`/api/connectors/${editing.id}`);
                    setEditing(null);
                    await load();
                  })
                }
              >
                Delete
              </Button>
            </>
          )
        }
      >
        {editing && (
          <ManageConnector
            connector={editing}
            entry={entryFor(editing)}
            me={me}
            onSaved={load}
            busy={busy}
            run={run}
            error={error}
            banner={
              <>
                {!editing.enabled && <p className="rounded-xl bg-warning/10 px-3 py-2 text-sm text-warning">Off: agents can't use this connector. Everything here stays editable, so you can fix it before switching it back on.</p>}
                {isLegacyDrive(editing) && <p className="rounded-xl bg-danger/10 px-3 py-2 text-sm text-danger">{LEGACY_DRIVE_REASON}</p>}
              </>
            }
          />
        )}
      </Dialog>

      <Dialog open={custom} onClose={() => setCustom(false)} size="lg" title="Custom API connector">
        <CustomConnectorForm
          onCreated={async (c) => {
            setCustom(false);
            await load();
            setEditing(c);
          }}
        />
      </Dialog>
    </div>
  );
}

function CustomConnectorForm({ onCreated }: { onCreated: (c: ConnectorDTO) => void }) {
  const [form, setForm] = useState({ name: "", slug: "", baseUrl: "https://", authType: "bearer", authConfig: '{\n  "test_path": "/"\n}', secrets: '{\n  "token": ""\n}', usageGuide: "" });
  const [sendProvenance, setSendProvenance] = useState(false);
  // A custom tool declares a personal token by its steps: where each person makes one carrying their own access there.
  const [personalSteps, setPersonalSteps] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const set = (k: keyof typeof form, v: string) => setForm({ ...form, [k]: v });
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label="Name"><input className={inputCls} value={form.name} onChange={(e) => set("name", e.target.value)} /></Field>
        <Field label="Slug"><input className={`${inputCls} font-mono`} value={form.slug} onChange={(e) => set("slug", e.target.value)} /></Field>
      </div>
      <Field label="Base URL"><input className={`${inputCls} font-mono`} value={form.baseUrl} onChange={(e) => set("baseUrl", e.target.value)} /></Field>
      <Field label="Auth type">
        <select className={inputCls} value={form.authType} onChange={(e) => set("authType", e.target.value)}>
          <option value="bearer">Bearer token</option>
          <option value="header">API key header</option>
          <option value="basic">Basic auth (username + password)</option>
          <option value="oauth2">OAuth2</option>
        </select>
      </Field>
      <Field label="Auth config (JSON)" hint='header: {"header":"x-api-key"} · basic: secrets {"username","password"} · oauth2: authorize_url, token_url, scopes'>
        <textarea className={`${inputCls} font-mono text-xs`} rows={4} value={form.authConfig} onChange={(e) => set("authConfig", e.target.value)} />
      </Field>
      <Field label="Secrets (JSON)">
        <textarea className={`${inputCls} font-mono text-xs`} rows={3} value={form.secrets} onChange={(e) => set("secrets", e.target.value)} />
      </Field>
      <Field label="Usage guide for agents">
        <textarea className={`${inputCls} font-mono text-xs`} rows={5} value={form.usageGuide} onChange={(e) => set("usageGuide", e.target.value)} />
        <AgentLineWarning text={form.usageGuide} what="tool" />
      </Field>
      <Switch
        label="Tell this tool which agent and run is calling"
        hint="X-Agent, X-Run and X-Scope, set by the platform; for our own tools."
        checked={sendProvenance}
        onChange={setSendProvenance}
      />
      {form.authType !== "oauth2" && (
        <Switch
          label="People can use a token of their own"
          hint="Each person pastes a token of theirs on My connections; switch people to it in Manage."
          checked={personalSteps !== null}
          onChange={(on) => setPersonalSteps(on ? "" : null)}
        />
      )}
      {personalSteps !== null && form.authType !== "oauth2" && (
        <Field label="Where each person makes their token">
          <textarea className={inputCls} rows={2} value={personalSteps} onChange={(e) => setPersonalSteps(e.target.value)} placeholder="Settings → API tokens → New token" />
        </Field>
      )}
      <ErrorText error={error} />
      <Button
        variant="primary"
        onClick={async () => {
          setError(null);
          try {
            const created = await post<ConnectorDTO>("/api/connectors", {
              name: form.name, slug: form.slug, baseUrl: form.baseUrl, authType: form.authType,
              authConfig: {
                ...JSON.parse(form.authConfig || "{}"),
                ...(personalSteps?.trim() && form.authType !== "oauth2" ? { personal_token: { steps: personalSteps.trim() } } : {}),
              },
              secrets: JSON.parse(form.secrets || "{}"), usageGuide: form.usageGuide,
              sendProvenance,
            });
            onCreated(created);
          } catch (err) {
            setError(err instanceof SyntaxError ? "Auth config and secrets must be valid JSON" : (err as Error).message);
          }
        }}
      >
        Create connector
      </Button>
    </div>
  );
}
